import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronUp, ChevronDown, Brain, TrendingUp, BarChart3, FileText,
  Search, Filter, RefreshCw, Clock, Tag, Star, CheckCircle2, X, Check,
  AlertTriangle, Terminal, Shield, ThumbsUp, ThumbsDown, Wrench,
  Flame, Snowflake, Zap, Activity, Cpu, Play, Copy, AlertCircle,
  History, MessageSquare, Plus, Trash2, Lock, Unlock, RotateCcw
} from 'lucide-react';
import { llmTracesDb, claudeMemoriesDb, ClaudeMemory, MemorySortOption } from '../../lib/supabase';
import * as mpcStorage from '../../utils/mpcStorage';
import type { Conversation, ConversationTurn, ConversationMode } from '../../utils/mpcStorage';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

// Memory stats type
interface MemoryStats {
  total: number;
  accessedToday: number;
  hot: number;
  cold: number;
  avgHelpful: number;
  recentlyRetrieved: ClaudeMemory[];
}

// Chart data types
interface ChartDataPoint {
  x: string | number;
  y: number;
}

interface MemoryChartData {
  accessTimeline: ChartDataPoint[];
  feedbackTimeline: { helpful: ChartDataPoint[]; unhelpful: ChartDataPoint[] };
  creationTimeline: ChartDataPoint[];
}

// Apex chart base options for dark theme
const getBaseChartOptions = (title: string): ApexOptions => ({
  chart: {
    type: 'area',
    background: 'transparent',
    toolbar: { show: false },
    sparkline: { enabled: false },
    animations: {
      enabled: true,
      speed: 800,
      animateGradually: { enabled: true, delay: 150 },
      dynamicAnimation: { enabled: true, speed: 350 }
    },
    zoom: { enabled: false },
  },
  theme: { mode: 'dark' },
  grid: {
    show: true,
    borderColor: 'rgba(255,255,255,0.05)',
    strokeDashArray: 3,
    padding: { left: 0, right: 0, top: 0, bottom: 0 }
  },
  stroke: { curve: 'smooth', width: 2 },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.05,
      stops: [0, 100]
    }
  },
  dataLabels: { enabled: false },
  xaxis: {
    type: 'category',
    labels: {
      style: { colors: '#6b7280', fontSize: '9px' },
      rotate: 0,
      hideOverlappingLabels: true,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: {
      style: { colors: '#6b7280', fontSize: '9px' },
      formatter: (val: number) => Math.round(val).toString()
    },
  },
  tooltip: {
    theme: 'dark',
    x: { show: true },
    y: { formatter: (val: number) => `${val}` }
  },
  title: {
    text: title,
    align: 'left',
    style: { fontSize: '11px', fontWeight: '500', color: '#9ca3af' }
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'right',
    fontSize: '10px',
    labels: { colors: '#9ca3af' },
    markers: { size: 6 },
  }
});

// CSS keyframes for memory flash animation
const memoryFlashKeyframes = `
@keyframes memoryFlash {
  0% {
    background-color: rgba(34, 211, 238, 0.5);
    border-color: rgb(34, 211, 238);
    box-shadow: 0 0 15px rgba(34, 211, 238, 0.4);
  }
  50% {
    background-color: rgba(34, 211, 238, 0.25);
    box-shadow: 0 0 8px rgba(34, 211, 238, 0.2);
  }
  100% {
    background-color: rgba(31, 41, 55, 0.5);
    border-color: rgb(55, 65, 81);
    box-shadow: none;
  }
}
`;

// Format timestamp to minute precision
const formatTimestamp = (ts: string | null) => {
  if (!ts) return 'Never';
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours}h ${mins}m ago` : `${hours}h ago`;
  }
  // Show date + time for older
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

interface BottomDrawerProps {
  logs: {
    stream: any[];
    corrections: any[];
    deviations: any[];
    overseer: any[];
  };
}

type DrawerTab = 'memories' | 'patterns' | 'logs' | 'compiler';

const getCategoryColor = (category: string) => {
  const colors: Record<string, string> = {
    gotcha: 'bg-red-500/20 text-red-400',
    decision: 'bg-blue-500/20 text-blue-400',
    deployment: 'bg-green-500/20 text-green-400',
    learning: 'bg-yellow-500/20 text-yellow-400',
    code: 'bg-purple-500/20 text-purple-400',
    general: 'bg-gray-500/20 text-gray-400',
    synapse: 'bg-cyan-500/20 text-cyan-400',
    dashboard: 'bg-pink-500/20 text-pink-400',
  };
  return colors[category] || 'bg-gray-500/20 text-gray-400';
};

// Heat level based on access count
const getHeatLevel = (accessCount: number): { icon: React.ReactNode; color: string; label: string } => {
  if (accessCount >= 50) return { icon: <Flame className="w-3 h-3" />, color: 'text-orange-400', label: 'Hot' };
  if (accessCount >= 20) return { icon: <Zap className="w-3 h-3" />, color: 'text-yellow-400', label: 'Warm' };
  if (accessCount >= 5) return { icon: <Activity className="w-3 h-3" />, color: 'text-gray-400', label: 'Active' };
  return { icon: <Snowflake className="w-3 h-3" />, color: 'text-blue-400', label: 'Cold' };
};

// Format relative time
const formatRelativeTime = (ts: string | null): string => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

// Glass panel wrapper component - matches SystemWidgets style
const GlassPanel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`relative overflow-hidden ${className}`}>
    {/* Glass background */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl" />
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5" />
    {/* Glass border */}
    <div className="absolute inset-0 border-t border-white/10" />
    {/* Shine effect */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50" />
    {/* Content */}
    <div className="relative h-full flex flex-col">{children}</div>
  </div>
);

export const BottomDrawer: React.FC<BottomDrawerProps> = ({ logs }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<DrawerTab>('memories');
  const [memories, setMemories] = useState<ClaudeMemory[]>([]);
  const [memorySearch, setMemorySearch] = useState('');
  const [memoryFilter, setMemoryFilter] = useState('all');
  const [memorySort, setMemorySort] = useState<MemorySortOption>('recent');
  const [patterns, setPatterns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [logType, setLogType] = useState<'stream' | 'corrections' | 'deviations' | 'overseer'>('stream');
  const [newMemoryIds, setNewMemoryIds] = useState<Set<string>>(new Set());
  const seenMemoryIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);
  const [stats, setStats] = useState<MemoryStats>({ total: 0, accessedToday: 0, hot: 0, cold: 0, avgHelpful: 0, recentlyRetrieved: [] });
  const [showRecentPanel, setShowRecentPanel] = useState(false);
  const [chartData, setChartData] = useState<MemoryChartData>({
    accessTimeline: [],
    feedbackTimeline: { helpful: [], unhelpful: [] },
    creationTimeline: []
  });

  // MPC Compiler state
  const [compilerInput, setCompilerInput] = useState('');
  const [compilerOutput, setCompilerOutput] = useState<any>(null);
  const [compilerError, setCompilerError] = useState<string | null>(null);
  const [compilerLoading, setCompilerLoading] = useState(false);
  const [compilerFormat, setCompilerFormat] = useState<'claude' | 'markdown' | 'json'>('claude');
  const [mpcStatus, setMpcStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [mpcStarting, setMpcStarting] = useState(false);

  // MPC Conversation/History state
  const [mpcSubTab, setMpcSubTab] = useState<'compose' | 'history'>('compose');
  const [conversationMode, setConversationMode] = useState<ConversationMode>(() => mpcStorage.getLastMode());
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedTurn, setSelectedTurn] = useState<ConversationTurn | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const inputSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check MPC health (via log-server proxy)
  const checkMpcHealth = useCallback(async () => {
    try {
      const res = await fetch('/api/claude-logs/api/mpc/health', {
        method: 'GET',
        signal: AbortSignal.timeout(2000)
      });
      setMpcStatus(res.ok ? 'online' : 'offline');
    } catch {
      setMpcStatus('offline');
    }
  }, []);

  // Check MPC status on mount and periodically when on compiler tab
  useEffect(() => {
    if (activeTab === 'compiler') {
      checkMpcHealth();
      const interval = setInterval(checkMpcHealth, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, checkMpcHealth]);

  // Load persisted input and conversations on mount
  useEffect(() => {
    setCompilerInput(mpcStorage.getLastInput());
    setConversations(mpcStorage.getAllConversations());
  }, []);

  // Debounced save of input (300ms)
  useEffect(() => {
    if (inputSaveTimeoutRef.current) {
      clearTimeout(inputSaveTimeoutRef.current);
    }
    inputSaveTimeoutRef.current = setTimeout(() => {
      mpcStorage.saveLastInput(compilerInput);
    }, 300);
    return () => {
      if (inputSaveTimeoutRef.current) {
        clearTimeout(inputSaveTimeoutRef.current);
      }
    };
  }, [compilerInput]);

  // Save mode when it changes
  useEffect(() => {
    mpcStorage.saveLastMode(conversationMode);
  }, [conversationMode]);

  // Reload conversations when switching to history tab
  useEffect(() => {
    if (mpcSubTab === 'history') {
      setConversations(mpcStorage.getAllConversations());
    }
  }, [mpcSubTab]);

  // Start/Stop MPC service (via log-server proxy)
  const toggleMpc = async () => {
    setMpcStarting(true);
    try {
      const action = mpcStatus === 'online' ? 'stop' : 'start';
      await fetch('/api/claude-logs/api/service-control', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: 'mpc', action })
      });
      // Wait a bit then check status
      setTimeout(checkMpcHealth, action === 'start' ? 3000 : 1000);
    } catch (e) {
      console.error('Failed to toggle MPC:', e);
    }
    setMpcStarting(false);
  };

  // Inject CSS keyframes on mount
  useEffect(() => {
    const styleId = 'memory-flash-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = memoryFlashKeyframes;
      document.head.appendChild(style);
    }
  }, []);

  // Load memory stats and chart data
  const loadStats = useCallback(async () => {
    try {
      // Get all memories for stats calculation
      const allMems = await claudeMemoriesDb.getSorted('recent', 500, 0);
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const fiveMinAgo = new Date(now.getTime() - 5 * 60 * 1000);

      const accessedToday = allMems.filter(m =>
        m.last_accessed && m.last_accessed.split('T')[0] === today
      ).length;

      const hot = allMems.filter(m => (m.access_count || 0) > 20).length;
      const cold = allMems.filter(m => (m.access_count || 0) < 3).length;

      const helpfulSum = allMems.reduce((sum, m) => sum + (m.metadata?.helpful_count || 0), 0);
      const unhelpfulSum = allMems.reduce((sum, m) => sum + (m.metadata?.unhelpful_count || 0), 0);
      const avgHelpful = helpfulSum + unhelpfulSum > 0
        ? Math.round((helpfulSum / (helpfulSum + unhelpfulSum)) * 100)
        : 50;

      // Recently retrieved (last 5 min)
      const recentlyRetrieved = allMems
        .filter(m => m.last_accessed && new Date(m.last_accessed) > fiveMinAgo)
        .slice(0, 10);

      setStats({
        total: allMems.length,
        accessedToday,
        hot,
        cold,
        avgHelpful,
        recentlyRetrieved
      });

      // Build chart data - last 7 days
      const days = 7;
      const accessTimeline: ChartDataPoint[] = [];
      const helpfulTimeline: ChartDataPoint[] = [];
      const unhelpfulTimeline: ChartDataPoint[] = [];
      const creationTimeline: ChartDataPoint[] = [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });

        // Count memories accessed on this day
        const accessedOnDay = allMems.filter(m =>
          m.last_accessed && m.last_accessed.split('T')[0] === dateStr
        ).length;
        accessTimeline.push({ x: dayLabel, y: accessedOnDay });

        // Count memories created on this day
        const createdOnDay = allMems.filter(m =>
          m.created_at && m.created_at.split('T')[0] === dateStr
        ).length;
        creationTimeline.push({ x: dayLabel, y: createdOnDay });

        // Sum feedback given on this day (approximate from last_helpful/last_unhelpful)
        const helpfulOnDay = allMems.filter(m =>
          m.metadata?.last_helpful && m.metadata.last_helpful.split('T')[0] === dateStr
        ).length;
        const unhelpfulOnDay = allMems.filter(m =>
          m.metadata?.last_unhelpful && m.metadata.last_unhelpful.split('T')[0] === dateStr
        ).length;
        helpfulTimeline.push({ x: dayLabel, y: helpfulOnDay });
        unhelpfulTimeline.push({ x: dayLabel, y: unhelpfulOnDay });
      }

      setChartData({
        accessTimeline,
        feedbackTimeline: { helpful: helpfulTimeline, unhelpful: unhelpfulTimeline },
        creationTimeline
      });
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
  }, []);

  // Load stats on mount and periodically
  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [loadStats]);

  // Handle memory updates with flash detection
  const updateMemoriesWithFlash = useCallback((newMems: ClaudeMemory[]) => {
    // Skip flash on initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      newMems.forEach(m => seenMemoryIdsRef.current.add(m.id));
      setMemories(newMems);
      return;
    }

    // Detect new memories
    const newIds = new Set<string>();
    newMems.forEach(m => {
      if (!seenMemoryIdsRef.current.has(m.id)) {
        newIds.add(m.id);
        seenMemoryIdsRef.current.add(m.id);
      }
    });

    if (newIds.size > 0) {
      setNewMemoryIds(prev => new Set([...prev, ...newIds]));
      // Clear flash after animation
      setTimeout(() => {
        setNewMemoryIds(prev => {
          const next = new Set(prev);
          newIds.forEach(id => next.delete(id));
          return next;
        });
      }, 2000);
    }

    setMemories(newMems);
  }, []);

  // Load data based on tab
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'memories':
          if (memorySearch) {
            const results = await claudeMemoriesDb.search(memorySearch, 50);
            updateMemoriesWithFlash(results);
          } else if (memoryFilter === 'penalized') {
            // Get memories sorted by unhelpful, then filter to penalized ones
            const allMems = await claudeMemoriesDb.getSorted('unhelpful', 200, 0);
            const penalized = allMems.filter(m => {
              const helpful = m.metadata?.helpful_count || 0;
              const unhelpful = m.metadata?.unhelpful_count || 0;
              return unhelpful > helpful && (helpful + unhelpful) >= 2;
            });
            updateMemoriesWithFlash(penalized.slice(0, 50));
          } else if (memoryFilter === 'trusted') {
            // Get memories sorted by helpful, then filter to highly trusted
            const allMems = await claudeMemoriesDb.getSorted('helpful', 200, 0);
            const trusted = allMems.filter(m => {
              const helpful = m.metadata?.helpful_count || 0;
              const unhelpful = m.metadata?.unhelpful_count || 0;
              const total = helpful + unhelpful;
              const ratio = total > 0 ? (helpful / total) * 100 : 50;
              return helpful >= 3 && ratio >= 70;
            });
            updateMemoriesWithFlash(trusted.slice(0, 50));
          } else if (memoryFilter !== 'all') {
            const filtered = await claudeMemoriesDb.getByCategory(memoryFilter, 50);
            updateMemoriesWithFlash(filtered);
          } else {
            const mems = await claudeMemoriesDb.getSorted(memorySort, 50, 0);
            updateMemoriesWithFlash(mems);
          }
          break;

        case 'patterns':
          try {
            const res = await fetch('/api/claude-logs/patterns');
            const data = await res.json();
            setPatterns(data.patterns || []);
          } catch {
            const dbPatterns = await llmTracesDb.getFailurePatterns(168, 2);
            setPatterns(dbPatterns);
          }
          break;
      }
    } catch (e) {
      console.error('Failed to load drawer data:', e);
    }
    setLoading(false);
  }, [activeTab, memorySearch, memoryFilter, memorySort]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search
  useEffect(() => {
    if (activeTab !== 'memories') return;
    const timer = setTimeout(loadData, 300);
    return () => clearTimeout(timer);
  }, [memorySearch, memoryFilter, memorySort]);

  const tabs: { id: DrawerTab; label: string; icon: React.ReactNode }[] = [
    { id: 'memories', label: 'Memories', icon: <Brain className="w-3.5 h-3.5" /> },
    { id: 'patterns', label: 'Patterns', icon: <TrendingUp className="w-3.5 h-3.5" /> },
    { id: 'logs', label: 'Raw Logs', icon: <FileText className="w-3.5 h-3.5" /> },
    { id: 'compiler', label: 'MPC', icon: <Cpu className="w-3.5 h-3.5" /> },
  ];

  // Compile handler for MPC
  const handleCompile = async () => {
    if (!compilerInput.trim()) return;
    setCompilerLoading(true);
    setCompilerError(null);
    setCompilerOutput(null);

    console.log('[MPC] Starting compile with input:', compilerInput.substring(0, 100));

    try {
      const res = await fetch('/api/claude-logs/api/mpc/compile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: compilerInput,
          format: compilerFormat
        })
      });

      console.log('[MPC] Response status:', res.status, res.ok);
      const data = await res.json();
      console.log('[MPC] Response data:', data);

      if (!res.ok) {
        console.error('[MPC] Error response:', data);
        setCompilerError(data.error || 'Compilation failed');
      } else {
        console.log('[MPC] Setting output:', data);
        setCompilerOutput(data);
      }
    } catch (e: any) {
      console.error('[MPC] Fetch error:', e);
      setCompilerError(`Connection failed: ${e.message}. Is MPC running on port 3200?`);
    }

    setCompilerLoading(false);
  };

  // MPC Conversation handlers
  const startNewConversation = useCallback(() => {
    const conv = mpcStorage.createConversation(conversationMode);
    setActiveConversation(conv);
    setConversations(mpcStorage.getAllConversations());
  }, [conversationMode]);

  const addTurnToConversation = useCallback((role: mpcStorage.TurnRole, content: string) => {
    if (!activeConversation) return;
    const turn = mpcStorage.addTurn(activeConversation.id, role, content);
    if (turn) {
      const updated = mpcStorage.getConversation(activeConversation.id);
      setActiveConversation(updated);
      setConversations(mpcStorage.getAllConversations());
    }
  }, [activeConversation]);

  const handleCompleteConversation = useCallback(() => {
    if (!activeConversation) return;
    mpcStorage.completeConversation(activeConversation.id);
    const updated = mpcStorage.getConversation(activeConversation.id);
    setActiveConversation(updated);
    setConversations(mpcStorage.getAllConversations());
  }, [activeConversation]);

  const handleReopenConversation = useCallback((convId: string) => {
    mpcStorage.reopenConversation(convId);
    const updated = mpcStorage.getConversation(convId);
    setActiveConversation(updated);
    setConversations(mpcStorage.getAllConversations());
  }, []);

  const handleDeleteConversation = useCallback((convId: string) => {
    mpcStorage.deleteConversation(convId);
    if (activeConversation?.id === convId) {
      setActiveConversation(null);
    }
    setConversations(mpcStorage.getAllConversations());
  }, [activeConversation]);

  const handleClearAllHistory = useCallback(() => {
    mpcStorage.clearAllHistory();
    setConversations([]);
    setActiveConversation(null);
    setCompilerInput('');
    setShowClearConfirm(false);
  }, []);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    });
  }, []);

  const logTabs: { id: typeof logType; label: string; count: number }[] = [
    { id: 'stream', label: 'Stream', count: logs.stream.length },
    { id: 'corrections', label: 'Corrections', count: logs.corrections.length },
    { id: 'deviations', label: 'Deviations', count: logs.deviations.length },
    { id: 'overseer', label: 'Overseer', count: logs.overseer.length },
  ];

  return (
    <GlassPanel className={`transition-all duration-300 ease-in-out ${isCollapsed ? 'h-10' : 'h-[200px] sm:h-[420px]'}`}>
      {/* Tab bar with stats - glass style */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 shrink-0 bg-white/[0.02]">
        {/* Collapse toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-all mr-2"
          title={isCollapsed ? 'Expand drawer' : 'Collapse drawer'}
        >
          {isCollapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        <div className="flex items-center gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white border border-white/10 shadow-lg shadow-cyan-500/10'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Live Stats Bar */}
        {activeTab === 'memories' && (
          <div className="flex items-center gap-3 text-[10px]">
            <div className="flex items-center gap-1 text-gray-400">
              <Brain className="w-3 h-3" />
              <span className="text-white font-medium">{stats.total}</span>
            </div>
            <div className="flex items-center gap-1 text-cyan-400" title="Accessed today">
              <Zap className="w-3 h-3" />
              <span>{stats.accessedToday}</span>
            </div>
            <div className="flex items-center gap-1 text-orange-400" title="Hot (>20 uses)">
              <Flame className="w-3 h-3" />
              <span>{stats.hot}</span>
            </div>
            <div className="flex items-center gap-1 text-blue-400" title="Cold (<3 uses)">
              <Snowflake className="w-3 h-3" />
              <span>{stats.cold}</span>
            </div>
            <div className="flex items-center gap-1" title="Helpful ratio">
              <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-green-400"
                  style={{ width: `${stats.avgHelpful}%` }}
                />
              </div>
              <span className="text-green-400">{stats.avgHelpful}%</span>
            </div>
            {stats.recentlyRetrieved.length > 0 && (
              <button
                onClick={() => setShowRecentPanel(!showRecentPanel)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded ${
                  showRecentPanel ? 'bg-cyan-500/20 text-cyan-400' : 'text-cyan-400/70 hover:text-cyan-400'
                }`}
                title="Recently retrieved"
              >
                <Activity className="w-3 h-3 animate-pulse" />
                <span>{stats.recentlyRetrieved.length} live</span>
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-cyan-400" />}
        </div>
      </div>

      {/* Content - hidden when collapsed */}
      <div className={`flex-1 overflow-hidden transition-all duration-300 ${isCollapsed ? 'opacity-0 invisible' : 'opacity-100 visible'}`}>
          {/* Memories Tab - 2 Column Layout */}
          {activeTab === 'memories' && (
            <div className="h-full flex">
              {/* LEFT COLUMN - Memory List */}
              <div className="w-1/2 h-full flex flex-col border-r border-white/5">
                {/* Filters - glass styled */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 shrink-0">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={memorySearch}
                      onChange={(e) => setMemorySearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                    />
                  </div>
                  <select
                    value={memoryFilter}
                    onChange={(e) => setMemoryFilter(e.target.value)}
                    className="px-2 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:border-cyan-500/50"
                  >
                    <option value="all">All</option>
                    <option value="penalized">Penalized</option>
                  <option value="trusted">Trusted</option>
                  <option value="gotcha">Gotcha</option>
                  <option value="decision">Decision</option>
                  <option value="learning">Learning</option>
                  <option value="deployment">Deployment</option>
                </select>
                <select
                  value={memorySort}
                  onChange={(e) => setMemorySort(e.target.value as MemorySortOption)}
                  className="px-2 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:border-cyan-500/50"
                >
                  <option value="recent">Recent</option>
                  <option value="last_used">Recently Used</option>
                  <option value="accessed">Most Used</option>
                  <option value="severity">Severity</option>
                  <option value="helpful">Most Helpful</option>
                  <option value="unhelpful">Most Unhelpful</option>
                </select>
              </div>
              {/* Recently Retrieved Panel (slides in from top) */}
              {showRecentPanel && stats.recentlyRetrieved.length > 0 && (
                <div className="border-b border-cyan-500/20 bg-cyan-500/5 px-3 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-3 h-3 text-cyan-400 animate-pulse" />
                    <span className="text-[10px] font-medium text-cyan-400">Just Retrieved (last 5 min)</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {stats.recentlyRetrieved.map(m => (
                      <div
                        key={m.id}
                        className="flex items-center gap-1 px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-[9px] text-cyan-300"
                        title={m.content}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          m.severity === 'critical' ? 'bg-red-500' :
                          m.severity === 'warning' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`} />
                        <span className="truncate max-w-[120px]">{m.content.slice(0, 30)}...</span>
                        <span className="text-cyan-500">{formatRelativeTime(m.last_accessed)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Memory list - COMPACT VIEW with glass hover */}
              <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-white/5">
                  {memories.map(mem => {
                    const helpfulCount = mem.metadata?.helpful_count || 0;
                    const unhelpfulCount = mem.metadata?.unhelpful_count || 0;
                    const totalVotes = helpfulCount + unhelpfulCount;
                    const helpfulRatio = totalVotes > 0 ? Math.round((helpfulCount / totalVotes) * 100) : 50;
                    const isImportant = mem.metadata?.important || false;
                    const isPenalized = unhelpfulCount > helpfulCount && totalVotes >= 2;
                    const isHighlyHelpful = helpfulCount >= 3;
                    const heat = getHeatLevel(mem.access_count || 0);
                    const isRecentlyAccessed = mem.last_accessed &&
                      (Date.now() - new Date(mem.last_accessed).getTime()) < 5 * 60 * 1000;

                    return (
                      <div
                        key={mem.id}
                        className={`flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 transition-all ${
                          isPenalized ? 'bg-red-500/5 border-l-2 border-l-red-500/50' :
                          isHighlyHelpful ? 'bg-green-500/5 border-l-2 border-l-green-500/50' :
                          isRecentlyAccessed ? 'bg-cyan-500/5 border-l-2 border-l-cyan-500/50' : ''
                        } ${newMemoryIds.has(mem.id) ? 'animate-[memoryFlash_2s_ease-out]' : ''}`}
                      >
                        {/* Heat indicator */}
                        <span className={`shrink-0 ${heat.color}`} title={`${heat.label} (${mem.access_count || 0} uses)`}>
                          {heat.icon}
                        </span>

                        {/* Category badge */}
                        <span className={`px-1 py-0.5 rounded text-[9px] font-medium shrink-0 ${getCategoryColor(mem.category)}`}>
                          {mem.category.slice(0, 3)}
                        </span>

                        {/* Severity indicator */}
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          mem.severity === 'critical' ? 'bg-red-500' :
                          mem.severity === 'warning' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`} title={mem.severity} />

                        {/* Content - truncated */}
                        <span className="flex-1 text-[11px] text-gray-300 truncate" title={mem.content}>
                          {mem.content}
                        </span>

                        {/* Last accessed */}
                        {mem.last_accessed && (
                          <span className={`text-[9px] shrink-0 ${isRecentlyAccessed ? 'text-cyan-400' : 'text-gray-600'}`}>
                            {formatRelativeTime(mem.last_accessed)}
                          </span>
                        )}

                        {/* Helpful ratio bar */}
                        {totalVotes > 0 && (
                          <div className="w-8 h-1 bg-gray-700 rounded-full overflow-hidden shrink-0" title={`${helpfulRatio}% helpful (${helpfulCount}/${totalVotes})`}>
                            <div
                              className={`h-full ${helpfulRatio >= 70 ? 'bg-green-500' : helpfulRatio >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${helpfulRatio}%` }}
                            />
                          </div>
                        )}

                        {/* Feedback counts */}
                        <div className="flex items-center gap-1 shrink-0 min-w-[40px] justify-end">
                          {helpfulCount > 0 && (
                            <span className="text-[9px] text-green-400">+{helpfulCount}</span>
                          )}
                          {unhelpfulCount > 0 && (
                            <span className="text-[9px] text-red-400">-{unhelpfulCount}</span>
                          )}
                        </div>

                        {/* Quick actions */}
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await fetch('/api/claude-logs/api/memories/feedback', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ memory_id: mem.id, helpful: true })
                                });
                                setMemories(prev => prev.map(m =>
                                  m.id === mem.id
                                    ? { ...m, metadata: { ...m.metadata, helpful_count: (m.metadata?.helpful_count || 0) + 1 } }
                                    : m
                                ));
                              } catch {}
                            }}
                            className="p-0.5 rounded text-gray-600 hover:text-green-400 hover:bg-green-500/10"
                            title="Helpful"
                          >
                            <ThumbsUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await fetch('/api/claude-logs/api/memories/feedback', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ memory_id: mem.id, helpful: false })
                                });
                                setMemories(prev => prev.map(m =>
                                  m.id === mem.id
                                    ? { ...m, metadata: { ...m.metadata, unhelpful_count: (m.metadata?.unhelpful_count || 0) + 1 } }
                                    : m
                                ));
                              } catch {}
                            }}
                            className="p-0.5 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10"
                            title="Unhelpful"
                          >
                            <ThumbsDown className="w-3 h-3" />
                          </button>
                          {isImportant && <Star className="w-3 h-3 text-yellow-400" fill="currentColor" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {memories.length === 0 && !loading && (
                  <div className="text-center text-gray-500 text-xs py-8">No memories found</div>
                )}
              </div>
              </div>

              {/* RIGHT COLUMN - Line Charts */}
              <div className="w-1/2 h-full flex flex-col overflow-hidden">
                <div className="flex-1 p-3 overflow-y-auto space-y-3">
                  {/* Memory Retrievals - Live */}
                  <div className="bg-white/[0.02] rounded-lg border border-white/5 p-3">
                    <ReactApexChart
                      options={{
                        ...getBaseChartOptions('Memory Retrievals (7d)'),
                        colors: ['#22d3ee'],
                      }}
                      series={[{
                        name: 'Retrievals',
                        data: chartData.accessTimeline
                      }]}
                      type="area"
                      height={100}
                    />
                  </div>

                  {/* Feedback Trends */}
                  <div className="bg-white/[0.02] rounded-lg border border-white/5 p-3">
                    <ReactApexChart
                      options={{
                        ...getBaseChartOptions('Feedback Loop'),
                        colors: ['#22c55e', '#ef4444'],
                      }}
                      series={[
                        { name: 'Helpful', data: chartData.feedbackTimeline.helpful },
                        { name: 'Unhelpful', data: chartData.feedbackTimeline.unhelpful }
                      ]}
                      type="area"
                      height={100}
                    />
                  </div>

                  {/* Memory Creation */}
                  <div className="bg-white/[0.02] rounded-lg border border-white/5 p-3">
                    <ReactApexChart
                      options={{
                        ...getBaseChartOptions('New Memories (7d)'),
                        colors: ['#a855f7'],
                      }}
                      series={[{
                        name: 'Created',
                        data: chartData.creationTimeline
                      }]}
                      type="area"
                      height={100}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Patterns Tab */}
          {activeTab === 'patterns' && (
            <div className="h-full overflow-y-auto p-4">
              {patterns.length === 0 ? (
                <div className="text-center text-gray-500 text-xs py-8">No patterns detected</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                  {patterns.map((p, idx) => (
                    <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <span className="text-xs font-medium text-red-400">
                          {p.pattern_description || `${p.preceding_tool} → ${p.failing_tool}`}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">{p.occurrence_count || 0} occurrences</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="h-full flex flex-col">
              {/* Log type tabs - glass pills */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5 shrink-0">
                {logTabs.map(lt => (
                  <button
                    key={lt.id}
                    onClick={() => setLogType(lt.id)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      logType === lt.id
                        ? 'bg-white/10 text-white border border-white/10'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {lt.label} ({lt.count})
                  </button>
                ))}
              </div>
              {/* Log content */}
              <div className="flex-1 overflow-y-auto p-2 font-mono text-xs">
                {logs[logType].slice(-50).map((entry, idx) => {
                  const line = typeof entry === 'string' ? entry : entry.line || JSON.stringify(entry);
                  return (
                    <div key={idx} className="py-0.5 text-gray-400 hover:bg-white/5 px-2 rounded truncate transition-colors">
                      {line}
                    </div>
                  );
                })}
                {logs[logType].length === 0 && (
                  <div className="text-center text-gray-500 py-8">No logs</div>
                )}
              </div>
            </div>
          )}

          {/* Compiler Tab - MPC */}
          {activeTab === 'compiler' && (
            <div className="h-full flex flex-col">
              {/* Sub-tabs and controls bar */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 shrink-0">
                <div className="flex items-center gap-2">
                  {/* Sub-tabs */}
                  <button
                    onClick={() => setMpcSubTab('compose')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                      mpcSubTab === 'compose'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <MessageSquare className="w-3 h-3" />
                    Compose
                  </button>
                  <button
                    onClick={() => setMpcSubTab('history')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                      mpcSubTab === 'history'
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <History className="w-3 h-3" />
                    History
                    {conversations.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-white/10 rounded text-[9px]">
                        {conversations.length}
                      </span>
                    )}
                  </button>

                  <div className="w-px h-4 bg-white/10" />

                  {/* Mode toggle */}
                  <button
                    onClick={() => setConversationMode(conversationMode === 'normal' ? 'back-and-forth' : 'normal')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                      conversationMode === 'back-and-forth'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                    title={conversationMode === 'back-and-forth' ? 'Back-and-forth mode active' : 'Normal mode'}
                  >
                    <RotateCcw className="w-3 h-3" />
                    {conversationMode === 'back-and-forth' ? 'B&F Mode' : 'Normal'}
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  {/* MPC Status */}
                  <button
                    onClick={toggleMpc}
                    disabled={mpcStarting || mpcStatus === 'checking'}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                      mpcStatus === 'online'
                        ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                        : mpcStatus === 'offline'
                        ? 'bg-red-500/20 border border-red-500/30 text-red-400'
                        : 'bg-gray-500/20 border border-gray-500/30 text-gray-400'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${
                      mpcStatus === 'online' ? 'bg-green-400' :
                      mpcStatus === 'offline' ? 'bg-red-400' : 'bg-gray-400 animate-pulse'
                    }`} />
                    {mpcStatus === 'online' ? 'Online' : mpcStatus === 'offline' ? 'Offline' : '...'}
                  </button>

                  {/* Clear history button */}
                  {conversations.length > 0 && (
                    <button
                      onClick={() => setShowClearConfirm(true)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                      title="Clear all history"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Clear confirmation dialog */}
              {showClearConfirm && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-gray-800 border border-white/10 rounded-lg p-4 max-w-sm mx-4">
                    <div className="flex items-center gap-2 text-red-400 mb-3">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">Clear All History?</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-4">
                      This will permanently delete all conversations, turns, and reset your saved input and mode preferences.
                    </p>
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setShowClearConfirm(false)}
                        className="px-3 py-1.5 text-xs text-gray-400 hover:text-white rounded-lg transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleClearAllHistory}
                        className="px-3 py-1.5 text-xs bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-all"
                      >
                        Clear Everything
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Compose Sub-tab */}
              {mpcSubTab === 'compose' && (
                <div className="flex-1 flex overflow-hidden">
                  {/* LEFT - Input */}
                  <div className="w-1/2 h-full flex flex-col border-r border-white/5">
                    {/* Compiler controls */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 shrink-0">
                      <select
                        value={compilerFormat}
                        onChange={(e) => setCompilerFormat(e.target.value as 'claude' | 'markdown' | 'json')}
                        className="px-2 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:border-cyan-500/50"
                      >
                        <option value="claude">Claude</option>
                        <option value="markdown">Markdown</option>
                        <option value="json">JSON</option>
                      </select>
                      <button
                        onClick={handleCompile}
                        disabled={compilerLoading || !compilerInput.trim() || mpcStatus !== 'online'}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-xs text-cyan-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {compilerLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        Compile
                      </button>
                      {compilerOutput?.success && (
                        <span className="flex items-center gap-1 text-[10px] text-green-400">
                          <CheckCircle2 className="w-3 h-3" />
                          Valid
                        </span>
                      )}
                    </div>

                    {/* Back-and-forth mode controls */}
                    {conversationMode === 'back-and-forth' && (
                      <div className="px-3 py-2 border-b border-purple-500/20 bg-purple-500/5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] text-purple-400 font-medium uppercase tracking-wider">
                            Back-and-Forth Session
                          </span>
                          {activeConversation ? (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400">
                                {activeConversation.turns.length} turns
                              </span>
                              {activeConversation.status === 'active' ? (
                                <button
                                  onClick={handleCompleteConversation}
                                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-green-500/20 text-green-400 border border-green-500/30 rounded hover:bg-green-500/30 transition-all"
                                >
                                  <Check className="w-3 h-3" />
                                  Mark Complete
                                </button>
                              ) : (
                                <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-gray-500/20 text-gray-400 border border-gray-500/30 rounded">
                                  <Lock className="w-3 h-3" />
                                  Completed
                                </span>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={startNewConversation}
                              className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded hover:bg-purple-500/30 transition-all"
                            >
                              <Plus className="w-3 h-3" />
                              Start Session
                            </button>
                          )}
                        </div>

                        {activeConversation && activeConversation.status === 'active' && (
                          <div className="flex items-center gap-2">
                            {mpcStorage.getExpectedNextRole(activeConversation) === 'chatgpt-question' ? (
                              <button
                                onClick={() => {
                                  if (compilerInput.trim()) {
                                    addTurnToConversation('chatgpt-question', compilerInput);
                                    setCompilerInput('');
                                  }
                                }}
                                disabled={!compilerInput.trim()}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-xs text-blue-400 font-medium disabled:opacity-50 transition-all"
                              >
                                <Plus className="w-3 h-3" />
                                Add ChatGPT Question
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  if (compilerInput.trim()) {
                                    addTurnToConversation('claude-response', compilerInput);
                                    setCompilerInput('');
                                  }
                                }}
                                disabled={!compilerInput.trim()}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-lg text-xs text-orange-400 font-medium disabled:opacity-50 transition-all"
                              >
                                <Plus className="w-3 h-3" />
                                Add Claude Response
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Input textarea */}
                    <div className="flex-1 p-3">
                      <textarea
                        value={compilerInput}
                        onChange={(e) => setCompilerInput(e.target.value)}
                        placeholder={conversationMode === 'back-and-forth'
                          ? "Paste ChatGPT question or Claude response here..."
                          : "Enter raw task description...\n\nExamples:\n• Build a REST API for user authentication\n• Create a CLI tool that parses JSON files\n• Implement rate limiting middleware"}
                        className="w-full h-full resize-none bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 font-mono"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.metaKey) {
                            handleCompile();
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* RIGHT - Output or Active Conversation */}
                  <div className="w-1/2 h-full flex flex-col overflow-hidden">
                    {conversationMode === 'back-and-forth' && activeConversation && activeConversation.turns.length > 0 ? (
                      /* Show conversation turns */
                      <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
                          Conversation ({activeConversation.turns.length} turns)
                        </div>
                        {activeConversation.turns.map((turn, idx) => (
                          <div
                            key={turn.id}
                            className={`p-2 rounded-lg border ${
                              turn.role === 'chatgpt-question'
                                ? 'bg-blue-500/10 border-blue-500/20'
                                : 'bg-orange-500/10 border-orange-500/20'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[10px] font-medium ${
                                turn.role === 'chatgpt-question' ? 'text-blue-400' : 'text-orange-400'
                              }`}>
                                {turn.role === 'chatgpt-question' ? 'ChatGPT → Claude' : 'Claude → Response'}
                              </span>
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] text-gray-500">
                                  {mpcStorage.formatTimestamp(turn.timestamp)}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(turn.content)}
                                  className="p-1 text-gray-500 hover:text-white rounded transition-all"
                                  title="Copy content"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className="text-[11px] text-gray-300 font-mono whitespace-pre-wrap line-clamp-3">
                              {turn.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Show compiler output */
                      <>
                        {compilerError && (
                          <div className="m-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <div className="flex items-center gap-2 text-red-400 text-xs font-medium mb-1">
                              <AlertCircle className="w-4 h-4" />
                              Compilation Error
                            </div>
                            <div className="text-[11px] text-red-300 font-mono">{compilerError}</div>
                          </div>
                        )}

                        {compilerOutput && (
                          <div className="flex-1 flex flex-col overflow-hidden">
                            {compilerOutput.validation && !compilerOutput.validation.pass && (
                              <div className="m-3 mb-0 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                <div className="text-[10px] text-yellow-400 font-medium mb-1">Validation Warnings</div>
                                {compilerOutput.validation.violations.map((v: string, i: number) => (
                                  <div key={i} className="text-[10px] text-yellow-300 font-mono">• {v}</div>
                                ))}
                              </div>
                            )}

                            <div className="flex-1 overflow-hidden flex flex-col m-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Compiled Output</span>
                                <button
                                  onClick={() => copyToClipboard(compilerOutput.rendered || JSON.stringify(compilerOutput.spec, null, 2))}
                                  className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-400 hover:text-white hover:bg-white/5 rounded transition-all"
                                >
                                  <Copy className="w-3 h-3" />
                                  Copy
                                </button>
                              </div>
                              <div className="flex-1 overflow-y-auto bg-white/[0.02] border border-white/5 rounded-lg p-3">
                                <pre className="text-[11px] text-gray-300 font-mono whitespace-pre-wrap">
                                  {compilerOutput.rendered || JSON.stringify(compilerOutput.spec, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}

                        {!compilerOutput && !compilerError && (
                          <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">
                            <div className="text-center">
                              <Cpu className="w-8 h-8 mx-auto mb-2 opacity-30" />
                              <div>Enter a task description and click Compile</div>
                              <div className="text-[10px] mt-1 text-gray-600">⌘+Enter to compile</div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* History Sub-tab */}
              {mpcSubTab === 'history' && (
                <div className="flex-1 flex overflow-hidden">
                  {/* LEFT - Conversation list */}
                  <div className="w-1/2 h-full flex flex-col border-r border-white/5 overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {conversations.length === 0 ? (
                        <div className="flex-1 flex items-center justify-center text-gray-500 text-xs h-full">
                          <div className="text-center py-8">
                            <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <div>No conversation history</div>
                            <div className="text-[10px] mt-1 text-gray-600">
                              Start a back-and-forth session to build history
                            </div>
                          </div>
                        </div>
                      ) : (
                        conversations.map(conv => (
                          <div
                            key={conv.id}
                            onClick={() => {
                              setActiveConversation(conv);
                              setSelectedTurn(conv.turns[0] || null);
                            }}
                            className={`p-2 rounded-lg border cursor-pointer transition-all ${
                              activeConversation?.id === conv.id
                                ? 'bg-cyan-500/10 border-cyan-500/30'
                                : 'bg-white/[0.02] border-white/5 hover:bg-white/5'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                  conv.mode === 'back-and-forth'
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : 'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {conv.mode === 'back-and-forth' ? 'B&F' : 'Normal'}
                                </span>
                                {conv.status === 'completed' ? (
                                  <Lock className="w-3 h-3 text-gray-500" />
                                ) : (
                                  <Unlock className="w-3 h-3 text-green-400" />
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] text-gray-500">
                                  {mpcStorage.formatTimestamp(conv.updatedAt)}
                                </span>
                                {conv.status === 'completed' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReopenConversation(conv.id);
                                    }}
                                    className="p-1 text-gray-500 hover:text-cyan-400 rounded transition-all"
                                    title="Reopen conversation"
                                  >
                                    <Unlock className="w-3 h-3" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteConversation(conv.id);
                                  }}
                                  className="p-1 text-gray-500 hover:text-red-400 rounded transition-all"
                                  title="Delete conversation"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {conv.turns.length} turn{conv.turns.length !== 1 ? 's' : ''}
                            </div>
                            {conv.turns.length > 0 && (
                              <div className="text-[11px] text-gray-500 truncate mt-1">
                                {conv.turns[0].content.slice(0, 60)}...
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* RIGHT - Turn details */}
                  <div className="w-1/2 h-full flex flex-col overflow-hidden">
                    {activeConversation ? (
                      <>
                        {/* Turn list */}
                        <div className="flex-1 overflow-y-auto p-2 space-y-1">
                          {activeConversation.turns.map((turn, idx) => (
                            <div
                              key={turn.id}
                              onClick={() => setSelectedTurn(turn)}
                              className={`p-2 rounded-lg border cursor-pointer transition-all ${
                                selectedTurn?.id === turn.id
                                  ? 'bg-white/10 border-white/20'
                                  : 'bg-white/[0.02] border-white/5 hover:bg-white/5'
                              } ${
                                turn.role === 'chatgpt-question'
                                  ? 'border-l-2 border-l-blue-500'
                                  : 'border-l-2 border-l-orange-500'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className={`text-[10px] font-medium ${
                                  turn.role === 'chatgpt-question' ? 'text-blue-400' : 'text-orange-400'
                                }`}>
                                  #{idx + 1} {turn.role === 'chatgpt-question' ? 'ChatGPT' : 'Claude'}
                                </span>
                                <div className="flex items-center gap-1">
                                  <span className="text-[9px] text-gray-500">
                                    {mpcStorage.formatTimestamp(turn.timestamp)}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(turn.content);
                                    }}
                                    className="p-1 text-gray-500 hover:text-white rounded transition-all"
                                    title="Copy content"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              <div className="text-[11px] text-gray-400 truncate mt-1">
                                {turn.content.slice(0, 80)}...
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Selected turn detail */}
                        {selectedTurn && (
                          <div className="border-t border-white/5 p-3 max-h-[40%] overflow-hidden flex flex-col">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-[10px] font-medium ${
                                selectedTurn.role === 'chatgpt-question' ? 'text-blue-400' : 'text-orange-400'
                              }`}>
                                {selectedTurn.role === 'chatgpt-question' ? 'ChatGPT Question' : 'Claude Response'}
                              </span>
                              <button
                                onClick={() => copyToClipboard(selectedTurn.content)}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-400 hover:text-white hover:bg-white/5 rounded transition-all"
                              >
                                <Copy className="w-3 h-3" />
                                Copy Full
                              </button>
                            </div>
                            <div className="flex-1 overflow-y-auto bg-white/[0.02] border border-white/5 rounded-lg p-2">
                              <pre className="text-[11px] text-gray-300 font-mono whitespace-pre-wrap">
                                {selectedTurn.content}
                              </pre>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">
                        <div className="text-center">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          <div>Select a conversation to view turns</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
    </GlassPanel>
  );
};

export default BottomDrawer;
